// Synthesised ringtones (WebAudio) used for web ringing and "Test sound".
// Browsers block audio until the user interacts with the page, so callers must
// call unlockAudio() from a click handler first.
import type { NotificationTone, RingtoneId } from '@mailpilot/shared';

export const RINGTONES: { id: RingtoneId; label: string }[] = [
  { id: 'chime', label: 'Chime' },
  { id: 'bell', label: 'Bell' },
  { id: 'pulse', label: 'Pulse' },
  { id: 'marimba', label: 'Marimba' },
  { id: 'alarm', label: 'Alarm' },
];

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}

export async function unlockAudio(): Promise<boolean> {
  const c = getCtx();
  if (!c) return false;
  try {
    if (c.state === 'suspended') await c.resume();
    // play a silent blip to fully unlock on Safari
    const o = c.createOscillator();
    const g = c.createGain();
    g.gain.value = 0;
    o.connect(g).connect(c.destination);
    o.start();
    o.stop(c.currentTime + 0.01);
    return c.state === 'running';
  } catch {
    return false;
  }
}

export function audioUnlocked(): boolean {
  return !!ctx && ctx.state === 'running';
}

type Voice = { freq: number; at: number; dur: number; type: OscillatorType; gain: number };

function note(c: AudioContext, out: AudioNode, v: Voice) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = v.type;
  o.frequency.setValueAtTime(v.freq, v.at);
  g.gain.setValueAtTime(0.0001, v.at);
  g.gain.exponentialRampToValueAtTime(v.gain, v.at + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, v.at + v.dur);
  o.connect(g).connect(out);
  o.start(v.at);
  o.stop(v.at + v.dur + 0.05);
}

/** One cycle of a pattern starting at t; returns its length in seconds. */
function pattern(c: AudioContext, out: AudioNode, id: RingtoneId, t: number): number {
  switch (id) {
    case 'chime':
      note(c, out, { freq: 1318.5, at: t, dur: 0.9, type: 'sine', gain: 0.6 });
      note(c, out, { freq: 1046.5, at: t + 0.28, dur: 1.1, type: 'sine', gain: 0.55 });
      return 1.8;
    case 'bell':
      [1, 2.76, 5.4].forEach((m, i) => note(c, out, { freq: 523.25 * m, at: t, dur: 1.8 - i * 0.4, type: 'sine', gain: 0.5 / (i + 1) }));
      return 2.2;
    case 'pulse':
      for (let i = 0; i < 3; i++) note(c, out, { freq: 880, at: t + i * 0.18, dur: 0.12, type: 'square', gain: 0.18 });
      return 1.2;
    case 'marimba':
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => note(c, out, { freq: f, at: t + i * 0.13, dur: 0.35, type: 'triangle', gain: 0.55 }));
      return 1.5;
    case 'alarm':
      for (let i = 0; i < 6; i++) note(c, out, { freq: i % 2 ? 720 : 960, at: t + i * 0.15, dur: 0.14, type: 'sawtooth', gain: 0.16 });
      return 1.3;
  }
}

/** Play a bounded ring session. Returns a stop function. */
export function playRingtone(id: RingtoneId, volume: number, durationSec: number): () => void {
  const c = getCtx();
  if (!c) return () => undefined;
  if (c.state === 'suspended') void c.resume();
  const master = c.createGain();
  master.gain.value = Math.max(0, Math.min(1, volume));
  master.connect(c.destination);
  let stopped = false;
  const end = c.currentTime + Math.max(1, durationSec);
  let t = c.currentTime + 0.05;
  // pre-schedule the whole bounded session
  while (t < end) {
    const len = pattern(c, master, id, t);
    t += len;
  }
  const timer = setTimeout(() => stop(), Math.max(1, durationSec) * 1000 + 2500);
  function stop() {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    try {
      master.gain.cancelScheduledValues(c!.currentTime);
      master.gain.setValueAtTime(master.gain.value, c!.currentTime);
      master.gain.linearRampToValueAtTime(0, c!.currentTime + 0.08);
      setTimeout(() => master.disconnect(), 150);
    } catch {
      /* ignore */
    }
  }
  return stop;
}

/** Voices for each notification tone (the default "chime" is the island's soft two-note cue). Also in desktop island.js. */
const TONE_VOICES: Record<Exclude<NotificationTone, 'none'>, (Omit<Voice, 'at'> & { offset: number })[]> = {
  chime: [
    { freq: 1174.66, dur: 0.22, type: 'sine', gain: 0.35, offset: 0 },
    { freq: 1567.98, dur: 0.32, type: 'sine', gain: 0.3, offset: 0.09 },
  ],
  bell: [
    { freq: 880, dur: 0.9, type: 'sine', gain: 0.32, offset: 0 },
    { freq: 1760, dur: 0.6, type: 'sine', gain: 0.12, offset: 0 },
    { freq: 2637, dur: 0.35, type: 'sine', gain: 0.05, offset: 0 },
  ],
  pop: [
    { freq: 520, dur: 0.08, type: 'triangle', gain: 0.4, offset: 0 },
    { freq: 780, dur: 0.1, type: 'triangle', gain: 0.32, offset: 0.06 },
  ],
  marimba: [
    { freq: 523.25, dur: 0.28, type: 'triangle', gain: 0.38, offset: 0 },
    { freq: 659.25, dur: 0.28, type: 'triangle', gain: 0.34, offset: 0.11 },
    { freq: 783.99, dur: 0.42, type: 'triangle', gain: 0.32, offset: 0.22 },
  ],
  ping: [{ freq: 1975.53, dur: 0.45, type: 'sine', gain: 0.28, offset: 0 }],
};

/** Play a notification tone (no-op until audio has been unlocked, or for "none"). */
export function playTone(tone: NotificationTone = 'chime', volume = 0.25): void {
  const c = ctx;
  if (!c || c.state !== 'running' || tone === 'none') return;
  const voices = TONE_VOICES[tone] ?? TONE_VOICES.chime;
  const out = c.createGain();
  out.gain.value = Math.max(0, Math.min(1, volume));
  out.connect(c.destination);
  const t = c.currentTime + 0.02;
  for (const { offset, ...v } of voices) note(c, out, { ...v, at: t + offset });
  setTimeout(() => out.disconnect(), 1500);
}

/** Short, soft two-note cue for the notification island (no-op until audio has been unlocked). */
export function playIslandChime(volume = 0.25): void {
  const c = ctx;
  if (!c || c.state !== 'running') return;
  const out = c.createGain();
  out.gain.value = Math.max(0, Math.min(1, volume));
  out.connect(c.destination);
  const t = c.currentTime + 0.02;
  note(c, out, { freq: 1174.66, at: t, dur: 0.22, type: 'sine', gain: 0.35 });
  note(c, out, { freq: 1567.98, at: t + 0.09, dur: 0.32, type: 'sine', gain: 0.3 });
  setTimeout(() => out.disconnect(), 800);
}
